import { NextRouter, useRouter } from "next/router";
import { Step } from "components/Step/stepTypes";

// @see https://www.typescriptlang.org/docs/handbook/2/conditional-types.html#inferring-within-conditional-types
type StepState<Type> = Type extends Step<infer X> ? X : never;

export type RouteOptions = {
  queries?: Record<string, string>;
  replace?: boolean;
  shallow?: boolean;
  stripQueries?: boolean;
};

export type UseStepReturnValue<T extends Step<StepState<T>>> = {
  currentSlug?: T["slug"];
  currentStep?: T;
  nextSlug?: T["slug"];
  prevSlug?: T["slug"];
  route?: (
    slug: T["slug"] | null,
    options?: RouteOptions
  ) => Promise<boolean | null>;
  router?: NextRouter;
  routePrevStep?: (options?: RouteOptions) => Promise<boolean | null>;
  routeNextStep?: (options?: RouteOptions) => Promise<boolean | null>;
  routeSkipPrevStep?: (options?: RouteOptions) => Promise<boolean | null>;
  routeSkipStep?: (options?: RouteOptions) => Promise<boolean | null>;
  routeFirstStep?: (options?: RouteOptions) => Promise<boolean | null>;
};

export function useStep<T extends Step<StepState<T>>>(
  steps: T[]
): UseStepReturnValue<T> {
  type Slug = T["slug"];
  const router = useRouter();
  const routeSlug = (router?.query.slug && String(router.query.slug)) as Slug;

  const currentStep = steps.find((s) => s.slug === routeSlug);
  if (!currentStep) {
    return {};
  }

  const slugs = steps.map((s) => s.slug);
  const currentSlug = currentStep.slug;
  const currentIndex = slugs.indexOf(currentStep.slug);
  const nextSlug = slugs[currentIndex + 1];
  const prevSlug = slugs[currentIndex - 1];

  async function route(slug: Slug | null, options?: RouteOptions) {
    if (!slug) {
      console.error(`${slug} slug does not exist.`);
      return null;
    }

    const {
      queries,
      replace = false,
      shallow = true,
      stripQueries = false,
    } = options || {};
    const routerQueries = stripQueries ? {} : router.query;

    const action = replace ? "replace" : "push";

    return await router?.[action]?.(
      { query: { ...routerQueries, slug, ...queries } },
      undefined,
      { shallow }
    );
  }

  async function routeFirstStep(options?: RouteOptions) {
    return route(steps[0].slug, options);
  }

  async function routeNextStep(options?: RouteOptions) {
    return route(nextSlug, options);
  }

  async function routeSkipPrevStep(options?: RouteOptions) {
    const skipSlug = steps[currentIndex - 2]?.slug;
    if (skipSlug) {
      return route(skipSlug, options);
    }
    console.error(`Unable to skip previous step: ${prevSlug}`);
    return routePrevStep(options);
  }

  async function routeSkipStep(options?: RouteOptions) {
    const skipSlug = steps[currentIndex + 2]?.slug;
    if (skipSlug) {
      return route(skipSlug, options);
    }
    console.error(`Unable to skip step: ${nextSlug}`);
    return routeNextStep(options);
  }

  async function routePrevStep(options?: RouteOptions) {
    return route(prevSlug, options);
  }

  return {
    currentSlug,
    currentStep,
    nextSlug,
    prevSlug,
    route,
    router,
    routePrevStep,
    routeNextStep,
    routeSkipPrevStep,
    routeSkipStep,
    routeFirstStep,
  };
}

export default useStep;
